import { useMemo } from "react";
import { CircleNotchIcon } from "@phosphor-icons/react";
import { useI18N } from "common/hooks";

import OnboardingCityCard from "./OnboardingCityCard";
import { useCityPhotos } from "../state/useCityPhotos";
import {
  cityKey,
  type OnboardingCityEntry,
  type OnboardingCityGroup,
  useOnboardingCities,
} from "../state/useOnboardingCities";

/**
 * Three columns, per design QA (DEV-523) — it was two, to keep cards wider
 * than ~90px at the docked sheet's 375px. The card's name wraps by word and
 * breaks a single long one, so the narrow column holds.
 */
const GRID_COLUMNS = 3;

interface OnboardingCityBrowseProps {
  /** A card was pressed. Resolving and committing it is the caller's. */
  onSelect: (city: OnboardingCityEntry) => void;
  /** `cityKey` of the card showing a spinner, if any. */
  selectingKey: string | null;
}

/**
 * The empty-search view of the city picker: every city we hold accessibility
 * data for, as photo cards grouped by country. Shared by onboarding's city
 * step and home's change-location modal, which design QA asked to look the
 * same.
 *
 * Its own data: the covered-cities facet and the Wikipedia photos are both
 * cached queries, so a caller that also reads `useOnboardingCities` does not
 * fetch twice.
 */
const OnboardingCityBrowse = ({
  onSelect,
  selectingKey,
}: OnboardingCityBrowseProps) => {
  const i18n = useI18N();
  const { browseGroups, totalCitiesWithData, isLoading } =
    useOnboardingCities();

  const cityNames = useMemo(
    () => browseGroups.flatMap((group) => group.cities.map((c) => c.name)),
    [browseGroups],
  );
  const photos = useCityPhotos(cityNames);

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <CircleNotchIcon
          size={24}
          aria-label={i18n.get("loading")}
          className="animate-spin text-fg-brand-strong"
        />
      </div>
    );
  }

  const renderGroup = (group: OnboardingCityGroup) => (
    <div key={group.country} className="flex flex-col gap-4">
      <h3 className="body-m-medium text-fg-neutral-strong">
        {`${group.country} (${group.cities.length})`}
      </h3>

      <div
        className="grid gap-4"
        style={{
          gridTemplateColumns: `repeat(${GRID_COLUMNS}, minmax(0, 1fr))`,
        }}
      >
        {group.cities.map((city) => (
          <OnboardingCityCard
            key={cityKey(city)}
            name={city.name}
            country={city.country}
            count={city.count}
            photoUrl={photos[city.name]}
            isSelecting={selectingKey === cityKey(city)}
            onSelect={() => onSelect(city)}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="-mx-4 bg-container-brand-minimal px-4 py-2">
        <h3 className="title-s-semibold text-fg-neutral-strong">
          {`${i18n.get("onboardingCityBrowseTitle")} (${totalCitiesWithData})`}
        </h3>
      </div>

      {browseGroups.map(renderGroup)}
    </div>
  );
};

export default OnboardingCityBrowse;
